import { useDashboardContext } from "@/contexts/DashboardContext";
import { Bar, CartesianGrid, ComposedChart, Label, Legend, Line, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from "recharts";
import styles from "./sales-breakdown-chart.module.css";
import { Payload as LegendPayload } from "recharts/types/component/DefaultLegendContent";
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import { SalesBreakdown } from "@boxem/core-api-schema";
import dayjs from "dayjs";
import { dayjsInPacificTZ, dayjsInUTC, isSameDay } from "@/utils/dateUtils";
import { formatValueAsCurrency, formatValueAsNumber, formatValueToAbbreviatedNumber } from "@/utils/formatUtils";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";

export type GroupedSalesBreakdownGranularity = 'quarter' | 'month' | 'week' | 'day' | 'hour';

export interface GroupedSalesBreakdown extends SalesBreakdown {
  xAxis: string;
  tooltipLabel: string;
  groupBy: GroupedSalesBreakdownGranularity;
}

const formatTooltipLabel = (date: Date | number | string, granularity: GroupedSalesBreakdownGranularity) => {
  switch (granularity) {
    case 'quarter':
      return dayjs(date).format('YYYY [Q]Q');
    case 'month':
      return dayjs(date).format('MMM YYYY');
    case 'week':
      return dayjs(date).format('MMM D');
    case 'day':
      return dayjs(date).format('MMM D');
    case 'hour':
      return dayjsInPacificTZ(date).format('MMM D, ha');
  }
}

const formatXAxis = (date: Date | number | string, granularity: GroupedSalesBreakdownGranularity) => {
  switch (granularity) {
    case 'quarter':
      return dayjs(date).format('YYYY [Q]Q');
    case 'month':
      return dayjs(date).format('MMM YYYY');
    case 'week':
      return dayjs(date).format('MMM D');
    case 'day':
      return dayjsInUTC(date).format('D');
    case 'hour':
      return dayjsInPacificTZ(date).format('ha');
  }
}

const getGroupByFromDateRange = (from: Date | number | string, to: Date | number | string): GroupedSalesBreakdownGranularity => {
  const startOfFromDate = dayjs(from).startOf('day');
  const toDate = dayjs(to);
  if (toDate.diff(startOfFromDate, 'month') <= 1) {
    return 'day';
  } else if (toDate.diff(startOfFromDate, 'month') <= 3) {
    return 'week';
  } else if (toDate.diff(startOfFromDate, 'year') <= 1) {
    return 'month';
  } else {
    return 'quarter';
  }
}

const groupSalesBreakdownByGranularity = (data: SalesBreakdown[]) => {
  const isDayGranularity = data[0].granularity === 'day';
  const groupBy = isDayGranularity ? getGroupByFromDateRange(data[0].date, data[data.length - 1].date) : 'hour';
  const groupedData = data.reduce((acc, item) => {
    const date = item.date//getUTCDateAsLocalDate(item.date).valueOf();
    if (!acc[date]) {
      acc[date] = {
        date,
        totalSales: 0,
        netProfit: 0,
        unitCount: 0,
        refundCount: 0,
        granularity: item.granularity,
        groupBy,
        tooltipLabel: formatTooltipLabel(date, groupBy),
        xAxis: formatXAxis(date, groupBy),
      }
    }
    acc[date]
    acc[date].totalSales += item.totalSales;
    acc[date].netProfit += item.netProfit > 0 ? item.netProfit : 0; // Only add positive net profit for now
    acc[date].unitCount += item.unitCount;
    acc[date].refundCount += item.refundCount;
    return acc;
  }, {} as Record<number, GroupedSalesBreakdown>);

  return Object.values(groupedData);
}

const ChartYAxisTick = ({ x, y, payload }) => {
  return (
    <text x={x} y={y}>
      {formatValueToAbbreviatedNumber(payload.value)}
    </text>
  );
}

interface ChartLegendPayload extends LegendPayload {
  dataKey?: string;
}

interface ChartLegendProps {
  payload?: ChartLegendPayload[]
}

const ChartLegend = ({ payload }: ChartLegendProps) => {
  const renderLegendColor = (dataKey: string) => {
    switch (dataKey) {
      case 'totalSales':
        return 'var(--primary-enabled)';
      case 'netProfit':
        return 'var(--sentiment-text-positive)';
      case 'unitCount':
        return '#5823FF';
      case 'refundCount':
        return 'var(--static-background-inverse-standard)';
      default:
        return 'black';
    }
  }
  return (
    <div className={styles.chartLegend}>
      {payload && payload.map((entry, index) => (
        <div key={`item-${index}`} className={styles.chartLegendItem}>
          <div className={styles.chartLegendColor} style={{ backgroundColor: renderLegendColor(entry.dataKey) }} />
          <div className={styles.chartLegendLabel}>{entry.value}</div>
        </div>
      ))}
    </div>
  );
}


const ChartTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles.chartTooltip}>
        <div className={styles.chartTooltipLabel}>{payload[0]?.payload?.tooltipLabel}</div>

        <div className={styles.chartTooltipData}><span className={styles.chartTooltipDataValue}>{formatValueAsCurrency(Number(payload[0].value))}</span><span className={styles.chartTooltipDataRevenue}>{payload[0].name}</span></div>

        <div className={styles.chartTooltipData}><span className={styles.chartTooltipDataValue}>{formatValueAsCurrency(Number(payload[1].value))}</span><span className={styles.chartTooltipDataProfit}>{payload[1].name}</span></div>

        <div className={styles.chartTooltipData}><span className={styles.chartTooltipDataValue}>{formatValueAsNumber(Number(payload[2].value))}</span><span className={styles.chartTooltipDataUnits}>{payload[2].name}</span></div>

        <div className={styles.chartTooltipData}><span className={styles.chartTooltipDataValue}>{formatValueAsNumber(Number(payload[3].value))}</span><span className={styles.chartTooltipDataRefunds}>{payload[3].name}</span></div>
      </div>
    );
  }
  return null;
};


export default function SalesBreakdownChart() {
  const { activeSnapshotIndex, salesSnapshotCards, salesBreakdownChart } = useDashboardContext();
  const { data: salesBreakdownData, isLoading } = salesBreakdownChart;

  const activeSnapshot = salesSnapshotCards[activeSnapshotIndex];
  const activeSnapshotInterval = activeSnapshot ? activeSnapshot.defaultInterval : null;

  const chartTitle = activeSnapshotInterval ? `${activeSnapshotInterval.label}, ${dayjs(activeSnapshotInterval.from).format('MMM D, YYYY')} ${activeSnapshotInterval.to && !isSameDay(activeSnapshotInterval.from, activeSnapshotInterval.to) ? ' - ' + dayjs(activeSnapshotInterval.to).format('MMM D, YYYY') : ''}` : "Sales Breakdown";

  const groupedSalesBreakdownData = salesBreakdownData && salesBreakdownData.length && groupSalesBreakdownByGranularity(salesBreakdownData);
  return (
    <div>
      <h2 className={styles.heading}>{chartTitle}</h2>
      <div className={styles.breakdownContent}>
        {isLoading ? (
          <div className={styles.chartLoader}>
            <LoadingSpinner size={"3rem"} />
          </div>
        ) : <>
          {groupedSalesBreakdownData ? (
            <ResponsiveContainer width="100%" height={360}>
              <ComposedChart
                width={500}
                height={400}
                data={groupedSalesBreakdownData}
                margin={{
                  top: 8,
                  right: 32,
                  left: 32,
                  bottom: 0,
                }}
              >
                <Tooltip content={(props) => <ChartTooltip {...props} />} />
                <Legend content={(props) => <ChartLegend {...props} />} />
                <YAxis yAxisId="revenue-profit" axisLine={false} tickLine={false} tick={ChartYAxisTick} className={`${styles.chartYAxis} ${styles.chartLeftYAxis}`}>
                  <Label angle={-90} value='Revenue/Profit' position='insideLeft' style={{ textAnchor: 'middle' }} />
                </YAxis>
                <Bar dataKey="totalSales" name="Revenue" className={styles.chartRevenueBar} yAxisId="revenue-profit" radius={[6, 6, 0, 0]} activeBar={{ className: styles.chartRevenueBarActive }} />
                <Line dataKey="netProfit" name="Profit" className={styles.chartProfitLine} dot={false} activeDot={{ fill: 'var(--sentiment-text-positive)', strokeWidth: 0, r: 6 }} yAxisId="revenue-profit" />

                <YAxis yAxisId="units-sold-refunds" axisLine={false} tickLine={false} orientation="right" tick={ChartYAxisTick} className={`${styles.chartYAxis} ${styles.chartRightYAxis}`}>
                  <Label angle={90} value='Units Sold/Refunds' position='insideRight' style={{ textAnchor: 'middle' }} />
                </YAxis>
                <Line dataKey="unitCount" name="Units sold" className={styles.chartUnitsLine} dot={false} activeDot={{ fill: '#5823FF', strokeWidth: 0, r: 6 }} yAxisId="units-sold-refunds" />
                <Line dataKey="refundCount" name="Refunds" className={styles.chartRefundsLine} dot={false} activeDot={{ fill: 'var(--static-background-inverse-standard)', strokeWidth: 0, r: 6 }} yAxisId="units-sold-refunds" />
                <XAxis dataKey="xAxis" tickLine={false} className={styles.chartXAxis} minTickGap={1} />
                <CartesianGrid vertical={false} className={styles.chartGrid} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div>No data available</div>
          )}</>}
      </div>
    </div>
  );
}